import { html, LitElement } from 'lit';
import Swiper, {
  Autoplay, Navigation, Pagination, Scrollbar, Thumbs,
} from 'swiper';

import('swiper/css');
import('swiper/css/autoplay');
import('swiper/css/navigation');
import('swiper/css/pagination');
import('swiper/css/scrollbar');
import('swiper/css/thumbs');

/**
 * BrandActive Slider WebComponent - a Swiper.js wrapper
 *
 * @example
 * <ba-slider slides-per-view="2" speed="1000" autoplay autoplay-delay="3000" loop>
 *       <div class="swiper">
 *         <!-- Additional required wrapper -->
 *         <div class="swiper-wrapper">
 *           <!-- Slides -->
 *           <div class="swiper-slide">Slide 1</div>
 *           <div class="swiper-slide">Slide 2</div>
 *           <div class="swiper-slide">Slide 3</div>
 *         </div>
 *       </div>
 *     </ba-slider>
 */
class BaSlider extends LitElement {
  static properties = {
    sliderName: { attribute: 'slider-nameee', type: String },
    slidesPerView: { attribute: 'slides-per-view', type: String },
    slidesPerGroup: { attribute: 'slides-per-group', type: Number },
    spaceBetween: { attribute: 'space-between', type: Number },
    effect: { type: String },
    loop: { type: Boolean },
    speed: { type: Number },
    centeredSlides: { attribute: 'centered-slides', type: Boolean },
    initialSlide: { attribute: 'initial-slide', type: Number },
    freeMode: { attribute: 'free-mode', type: Boolean },
    pagination: { type: Boolean },
    paginationElement: { attribute: 'pagination-element', type: String },
    paginationClickable: { attribute: 'pagination-clickable', type: Boolean },
    scrollBar: { type: Boolean },
    scrollBarElement: { attribute: 'scrollbar-element', type: String },
    autoplay: { type: Boolean },
    autoplayDelay: { attribute: 'autoplay-delay', type: Number },
    navigation: { type: Boolean },
    navigationElementNext: { attribute: 'navigation-element-next', type: String },
    navigationElementPrev: { attribute: 'navigation-element-prev', type: String },
    breakpoints: { type: Boolean },
    breakpointsSettings: { attribute: 'breakpoints-settings', type: Object },
    direction: { attribute: 'direction', type: String },
    watchSlidesProgress: { attribute: 'watch-slides-progress', type: Boolean },
    thumbsId: { attribute: 'thumbs-id', type: String },
  };

  /**
   * Global registry all instances initialized
   *
   * @type {*}
   * @private
   */
  static swiperInstances = new Map();

  /**
   * @name constructor
   * @kind function
   */
  constructor() {
    super();

    this.id = this.id || this.generateId();
    this.sliderName = '.swiper';
    this.slidesPerView = 1;
    this.spaceBetween = 10;
    this.slidesPerGroup = 1;
    this.effect = 'slide';
    this.loop = false;
    this.speed = 1000;
    this.centeredSlides = false;
    this.initialSlide = 0;
    this.freeMode = false;
    this.pagination = false;
    this.paginationElement = '.swiper-pagination';
    this.paginationClickable = false;
    this.scrollBar = false;
    this.scrollBarElement = '.swiper-scrollbar';
    this.autoplay = false;
    this.autoplayDelay = 3000;
    this.navigation = false;
    this.navigationElementNext = '.swiper-button-next';
    this.navigationElementPrev = '.swiper-button-prev';
    this.breakpoints = false;
    this.direction = 'horizontal';
    this.watchSlidesProgress = false;
    this.thumbsId = null;
    this.breakpointsSettings = {
      1024: {
        slidesPerView: this.slidesPerView,
        spaceBetween: this.spaceBetween,
      },
      768: {
        slidesPerView: this.slidesPerView,
        spaceBetween: this.spaceBetween,
      },
      375: {
        slidesPerView: this.slidesPerView,
        spaceBetween: this.spaceBetween,
      },
    };

    this.swiperInstance = null;
  }

  /**
   * Mapping function for Array.from
   *
   * @param {number} dec decimal
   * @returns {string} hex
   */
  // eslint-disable-next-line class-methods-use-this
  dec2hex(dec) {
    return dec.toString().padStart(2, '0');
  }

  /**
   * Generates (pseudo) unique Id
   *
   * @returns {string} Unique Id
   */
  generateId() {
    const arr = new Uint8Array(14);
    window.crypto.getRandomValues(arr);
    return Array.from(arr, this.dec2hex).join('');
  }

  /**
   *@kind function
   *@name createdSliderModule
   *@returns {Array} Array of Swiper's modules to load
   */
  getSwiperModules() {
    return [
      ...(this.autoplay ? [Autoplay] : []),
      ...(this.navigation ? [Navigation] : []),
      ...(this.pagination ? [Pagination] : []),
      ...(this.scrollBar ? [Scrollbar] : []),
      ...(this.thumbsId ? [Thumbs] : []),
    ];
  }

  /**
   *@kind function
   *@name getSwiperOptions
   *@returns {object} Swiper configuration options
   */
  getSwiperOptions() {
    return {
      slidesPerView: this.slidesPerView,
      slidesPerGroup: this.slidesPerGroup,
      spaceBetween: this.spaceBetween,
      effect: this.effect,
      loop: this.loop,
      speed: this.speed,
      centeredSlides: this.centeredSlides,
      initialSlide: this.initialSlide,
      freeMode: this.freeMode,
      direction: this.direction,
      watchSlidesProgress: this.watchSlidesProgress,
      ...(this.pagination && {
        pagination: {
          el: this.querySelector(this.paginationElement),
          clickable: this.paginationClickable,
        },
      }),
      ...(this.scrollBar && {
        scrollBar: {
          el: this.querySelector(this.scrollBarElement),
        },
      }),
      ...(this.autoplay && {
        autoplay: {
          delay: this.autoplayDelay,
        },
      }),
      ...(this.navigation && {
        navigation: {
          nextEl: this.querySelector(this.navigationElementNext),
          prevEl: this.querySelector(this.navigationElementPrev),
        },
      }),
      ...(this.breakpoints && {
        breakpoints: this.breakpointsSettings,
      }),
      ...(this.thumbsId && {
        thumbs: {
          swiper: this.constructor.swiperInstances.get(this.thumbsId),
        },
      }),
    };
  }

  /**
   * @name initSwiper
   * @kind function
   */
  initSwiper() {
    try {
      const swiperEl = this.querySelector(this.sliderName);
      if (typeof (swiperEl) === 'undefined' || swiperEl === null) {
        return;
      }
      if (!this.swiperInstance) {
        this.swiperInstance = new Swiper(
          swiperEl,
          {
            ...this.getSwiperOptions(),
            modules: this.getSwiperModules(),
          },
        );
      }
    } catch (e) {
      // Catch to silence errors
    }
  }

  /**
   * @kind function
   * @name connectedCallback
   */
  connectedCallback() {
    super.connectedCallback();
    this.initSwiper();
    this.constructor.swiperInstances.set(this.id, this.swiperInstance);
  }

  /**
   * @kind function
   * @name disconnectedCallback
   */
  disconnectedCallback() {
    super.connectedCallback();
    this.constructor.swiperInstances.delete(this.id);
  }

  /**
   *@kind function
   *@name render
   * @returns {*} Returns template
   */
  render() {
    return html`<slot></slot>`;
  }
}
customElements.define('ba-slider', BaSlider);

export default BaSlider;
